<template>
  <div class="select-media-container">
    <div class="flex gap-m">
      <div
        class="preview padding-s flex flex-stretch border-thin-gray-2 cursor-pointer"
        style="height: 100px; width: 150px"
        @click.stop.prevent="startSelectFileProcess">

        <img
          v-if="currentMedia"
          class="object-cover max-size-parent width-expand"
          :src="currentMedia.thumbUrl" />
        <div
          v-else
          class="image-placeholder flex flex-center flex-middle bg-gray-4 width-expand">
          <icon size="3" icon="image" theme="bg"></icon>
        </div>
      </div>
      <div class="controls" :style="isLoading ? `opacity:0.6;pointer-events:none;` : ``">
        <div class="upload-control flex flex-column flex-between" style="height: 100%">
          <form-button
            v-if="value"
            theme="alt"
            text="core.form.mediaSelectField.change"
            icon="refresh"
            @click.stop.prevent="startSelectFileProcess"></form-button>
          <form-button
            v-if="!!(value || localValue)"
            theme="danger"
            text="core.form.mediaSelectField.clear"
            icon="x"
            @click.stop.prevent="clear"></form-button>
          <form-button
            v-if="!value"
            theme="success"
            text="core.form.mediaSelectField.uploadCta"
            icon="cloud-upload"
            @click.stop.prevent="startSelectFileProcess"></form-button>
        </div>
        <file-input
          ref="fileUploadField"
          v-model="uploadFiles"
          class="hidden"
          multiple="false"
          :accept="accept"
          label="file upload" />
      </div>
    </div>
    <div class="message-container" style="min-height: 20px">
      <div class="message flex flex-middle flex-start gap-m margin-m-top">
        <spinner v-if="isLoading"></spinner>
        <icon-text v-if="internalErrorMessage" icon="warning-triangle" class="c-danger">{{
          internalErrorMessage
        }}</icon-text>
        <icon-text v-if="internalSuccessMessage" icon="check" class="c-success">{{
          internalSuccessMessage
        }}</icon-text>
        <span v-if="internalMessage">{{ internalMessage }}</span>
      </div>
    </div>

  </div>
</template>

<script>
import SimpleInputMixin from "@/client/extensions/mixins/baseComponents/form/controls/input/simpleInput.js";
import useMediaManager from "@/client/extensions/composition/useMediaManager";
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import { computed } from "vue";

export default {
  mixins: [SimpleInputMixin],
  props: {
    accept: {
      type : String,
      default: 'image/*',
    },
    maxImageWidth: {
      type: Number,
      default: 1920,
    },
    maxImageHeight: {
      type: Number,
      default: 1200,
    },
  },
  setup(props) {
    let { asyncOps, asyncOpsReady, asyncStatus } = asyncOperations(props);
    const mediaManager = useMediaManager(props, {});
    return { asyncOps, asyncOpsReady, asyncStatus, mediaManager };
  },
  data() {
    return {
      mediaItems: this.mediaManager.items,
      uploadFiles: false,
      isLoading: false,
      // allows us to work without a v-model
      localValue: false,
      internalErrorMessage: false,
      internalSuccessMessage: false,
      internalMessage: false,
      localMedia: false,
    };
  },
  computed: {
    value: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("field:changed", value);
        this.$emit("update:modelValue", value);
        if (value && value != "") {
          this.mediaManager.setLimit(1);
          this.mediaManager.setFilter("id", value);
          this.mediaManager.refreshListItems();
        }

        if ( ! value ) {
          this.localValue = null;
          this.localMedia = null;
        }
      },
    },
    currentMedia() {
      if (this.mediaItems && this.mediaItems.length > 0) {
        return this.mediaItems[0];
      }


      if (this.localMedia) {
        return this.localMedia;
      }
      return false;
    },
  },
  watch: {
    value: {
      handler(newVal, oldVal) {
        if (!newVal) {
          return true;
        }
        this.mediaManager.setLimit(1);
        this.mediaManager.setFilter("id", newVal);
        this.mediaManager.refreshListItems();
        return newVal;
      },
      deep: true,
      immediate: true,
    },
    uploadFiles(newVal) {
      if (!newVal || newVal.length < 1) {
        return true;
      }
      this.uploadFilesByState();
    },
    "mediaManager.items": {
      handler(newVal) {
        console.log("watch media manager files", newVal);
      },
    },
  },
  methods: {
    clear() {
      this.value = null;
      this.localValue = null;
      this.localMedia = null;
    },
    startSelectFileProcess() {
      try {
        this.$refs.fileUploadField.promptFileSelection();
      } catch (e) {
        warn("failed to focus file input", e);
      }
    },
     async uploadFilesByState() {
      let hasError = false;

      // basic validate the file size
      for (const file of this.uploadFiles) {
        if (file.size / 1000000 > config.mediaManager.maxFileSizeMB) {
          this.$s.ui.notification("media.manager.fileTooLargeNotification", "error", {
            contentParams: { maxSizeMB: config.mediaManager.maxFileSizeMB },
          });
          return false;
        }
      }

      this.isLoading = true;

      let uploadResult;

      for (const file of this.uploadFiles) {
        let uploadCandidate = file;
        if (this.mediaManager.isImage(file)) {
          this.internalMessage = this.t("core.form.mediaSelectField.processingOngoing");
          uploadCandidate = await this.mediaManager.resizeImage(file, this.maxImageWidth, this.maxImageHeight);
        }
        this.internalMessage = this.t("core.form.mediaSelectField.uploadInProgress");

        uploadResult = await this.mediaManager.upload(uploadCandidate, {
          data: this.uploadData,
        });

        this.internalMessage = false;

        if (!uploadResult) {
          hasError = true;
        }
      }

      if (hasError) {
        this.$s.ui.notification("media.manager.uploadErrorOccurred", "error");
        this.isLoading = false;
        return;
      }

      this.asyncOps.clearAllRequestDataFromCache();
      this.isLoading = false;

      this.value = uploadResult.id;
      this.localValue = uploadResult.id;
      this.localMedia = uploadResult;
    },
  },
};
</script>

<style scoped lang="scss"></style>
